import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { ArrowRight, CalendarDays, CheckCircle2, Inbox, Megaphone, Newspaper, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";

export default function AdminDashboard() {
  const { user } = useOutletContext();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api
      .get("/stats")
      .then((res) => setStats(res.data))
      .catch(() => setStats({ posts: 0, published_posts: 0, classes: 0, enquiries: 0, new_enquiries: 0 }));
  }, []);

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-display text-3xl sm:text-4xl font-bold">Hello, {user?.name?.split(" ")[0] || "Kerry"}!</h1>
      <p className="text-muted-foreground mt-2">Here&rsquo;s what&rsquo;s happening on your website.</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mt-8">
        {!stats ? (
          [...Array(4)].map((_, i) => <Skeleton key={i} className="h-32 rounded-[4px]" />)
        ) : (
          <>
            <Card className="rounded-[4px] border-[hsl(var(--kerrys-coral))]/50" data-testid="admin-stat-leads">
              <CardContent className="p-6">
                <span className="h-10 w-10 rounded-[4px] bg-[hsl(var(--kerrys-coral))]/15 flex items-center justify-center">
                  <Megaphone className="h-5 w-5 text-[hsl(18,80%,45%)]" />
                </span>
                <p className="font-display text-3xl font-bold mt-4 tabular-nums">{stats.new_leads ?? 0}</p>
                <p className="text-sm text-muted-foreground mt-1">New taster leads</p>
              </CardContent>
            </Card>
            <Card className="rounded-[4px]" data-testid="admin-stat-enquiries">
              <CardContent className="p-6">
                <span className="h-10 w-10 rounded-[4px] bg-[hsl(var(--kerrys-sun-pale))] flex items-center justify-center">
                  <Inbox className="h-5 w-5 text-[hsl(38,80%,35%)]" />
                </span>
                <p className="font-display text-3xl font-bold mt-4 tabular-nums">{stats.new_enquiries ?? 0}</p>
                <p className="text-sm text-muted-foreground mt-1">New costume enquiries</p>
              </CardContent>
            </Card>
            <Card className="rounded-[4px]" data-testid="admin-stat-posts">
              <CardContent className="p-6">
                <span className="h-10 w-10 rounded-[4px] bg-secondary flex items-center justify-center">
                  <Newspaper className="h-5 w-5 text-primary" />
                </span>
                <p className="font-display text-3xl font-bold mt-4 tabular-nums">{stats.posts}</p>
                <p className="text-sm text-muted-foreground mt-1">Blog posts</p>
              </CardContent>
            </Card>
            <Card className="rounded-[4px]" data-testid="admin-stat-classes">
              <CardContent className="p-6">
                <span className="h-10 w-10 rounded-[4px] bg-[hsl(var(--kerrys-coral))]/15 flex items-center justify-center">
                  <CalendarDays className="h-5 w-5 text-[hsl(18,80%,45%)]" />
                </span>
                <p className="font-display text-3xl font-bold mt-4 tabular-nums">{stats.classes}</p>
                <p className="text-sm text-muted-foreground mt-1">Timetable classes</p>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mt-8">
        <Card className="rounded-[4px] border-[hsl(var(--kerrys-coral))]/40">
          <CardContent className="p-6 sm:p-8">
            <h2 className="font-display text-xl sm:text-2xl font-semibold">Free taster leads</h2>
            <p className="text-sm text-muted-foreground mt-2">Parents who enquired via your Google/Meta ad landing page. Reply fast — you also get an email for each one.</p>
            <Button asChild className="rounded-[4px] mt-5 font-semibold" data-testid="admin-dashboard-leads-button">
              <Link to="/admin/leads">
                View leads <ArrowRight className="h-4 w-4 ml-1.5" />
              </Link>
            </Button>
          </CardContent>
        </Card>
        <Card className="rounded-[4px]">
          <CardContent className="p-6 sm:p-8">
            <h2 className="font-display text-xl sm:text-2xl font-semibold">Costume enquiries</h2>
            <p className="text-sm text-muted-foreground mt-2">See who's asking about uniforms and costumes, and mark them handled.</p>
            <Button asChild className="rounded-[4px] mt-5 font-semibold" data-testid="admin-dashboard-enquiries-button">
              <Link to="/admin/enquiries">
                View enquiries <ArrowRight className="h-4 w-4 ml-1.5" />
              </Link>
            </Button>
          </CardContent>
        </Card>
        <Card className="rounded-[4px]">
          <CardContent className="p-6 sm:p-8">
            <h2 className="font-display text-xl sm:text-2xl font-semibold">Write a blog post</h2>
            <p className="text-sm text-muted-foreground mt-2">Share exam results, show news and class spotlights with parents.</p>
            <Button asChild className="rounded-[4px] mt-5 font-semibold" data-testid="admin-dashboard-blog-button">
              <Link to="/admin/blog">
                Manage blog <ArrowRight className="h-4 w-4 ml-1.5" />
              </Link>
            </Button>
          </CardContent>
        </Card>
        <Card className="rounded-[4px]">
          <CardContent className="p-6 sm:p-8">
            <h2 className="font-display text-xl sm:text-2xl font-semibold">Reviews</h2>
            <p className="text-sm text-muted-foreground mt-2">Paste your best Google reviews — they show on your homepage and ad landing page.</p>
            <Button asChild className="rounded-[4px] mt-5 font-semibold" data-testid="admin-dashboard-reviews-button">
              <Link to="/admin/reviews">
                Manage reviews <ArrowRight className="h-4 w-4 ml-1.5" />
              </Link>
            </Button>
          </CardContent>
        </Card>
        <Card className="rounded-[4px]">
          <CardContent className="p-6 sm:p-8">
            <h2 className="font-display text-xl sm:text-2xl font-semibold">Update the timetable</h2>
            <p className="text-sm text-muted-foreground mt-2">Add, edit or remove classes — changes appear on the site instantly.</p>
            <Button asChild className="rounded-[4px] mt-5 font-semibold" data-testid="admin-dashboard-timetable-button">
              <Link to="/admin/timetable">
                Manage timetable <ArrowRight className="h-4 w-4 ml-1.5" />
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
